import React from 'react';
import styled from 'styled-components';

/**
 * The wiring picture for a MikroTik, drawn from the SAME port layout the
 * Step-2 chips show (so the two can never disagree), one picture per model
 * because the models differ in what the ports do:
 *
 *  - hEX RB750Gr3: VLAN isolation is on — ether1 internet, ether2/3 customers
 *    (an access point, or a device plugged straight in), ether4 trunk (a
 *    switch with the sub-vendo access points, each on its own VLAN), ether5
 *    the owner's laptop (plain internet, no portal). Benched 2026-10-06 on
 *    HEX - HYBRID: EAP110 on ether3, basic switch + two VLAN access points on
 *    ether4, the Mac on ether5.
 *  - RB4011 / CCR: run flat for now — the chosen WAN port is the internet and
 *    every other port serves customers; the picture says so and nothing more.
 *
 * Same idea as the mini PC's wiring tabs (Vendo/WiringSetups.tsx), in the
 * MikroTik's own terms. Ray, 2026-10-06: "add a setup illustration diagram like
 * minipc for the vlan guide … setup for hex, rb, ccr".
 */
export type WiringPortRole = 'wan' | 'customer' | 'trunk' | 'owner';
export interface WiringPort {
   port: string;
   role: WiringPortRole;
}

const ROLE: Record<WiringPortRole, { fill: string; stroke: string; text: string }> = {
   wan: { fill: '#f3f4f6', stroke: '#6b7280', text: '#374151' },
   customer: { fill: '#dcfce7', stroke: '#16a34a', text: '#166534' },
   trunk: { fill: '#dbeafe', stroke: '#2563eb', text: '#1e40af' },
   owner: { fill: '#ffedd5', stroke: '#ea580c', text: '#9a3412' },
};

const box = (x: number, y: number, w: number, h: number, role: WiringPortRole | 'plain', label: string, sub?: string, sub2?: string) => {
   const c = role === 'plain' ? { fill: '#fff', stroke: '#9ca3af', text: '#111827' } : ROLE[role];
   return (
      <g>
         <rect x={x} y={y} width={w} height={h} rx={8} fill={c.fill} stroke={c.stroke} strokeWidth={1.5} />
         <text x={x + w / 2} y={y + 18} textAnchor="middle" fontSize={12} fontWeight={700} fill="#111827">
            {label}
         </text>
         {sub && (
            <text x={x + w / 2} y={y + 33} textAnchor="middle" fontSize={10} fill="#4b5563">
               {sub}
            </text>
         )}
         {sub2 && (
            <text x={x + w / 2} y={y + 46} textAnchor="middle" fontSize={10} fill="#4b5563">
               {sub2}
            </text>
         )}
      </g>
   );
};
const line = (x1: number, y1: number, x2: number, y2: number, color: string) => (
   <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth={2} />
);

/** The router as a bar of its ports, each coloured by its job. Returns the x centre of every port. */
function routerBar(ports: WiringPort[], title: string): { nodes: React.ReactNode; centre: Record<string, number> } {
   const W = 640;
   const n = ports.length;
   const chip = Math.min(56, Math.floor((W - 80) / n) - 4);
   const total = n * (chip + 4) - 4;
   const x0 = (W - total) / 2;
   const centre: Record<string, number> = {};
   const nodes = (
      <g>
         <rect x={x0 - 14} y={14} width={total + 28} height={72} rx={10} fill="#f9fafb" stroke="#9ca3af" strokeWidth={1.5} />
         <text x={W / 2} y={32} textAnchor="middle" fontSize={12} fontWeight={700} fill="#111827">
            {title}
         </text>
         {ports.map((p, i) => {
            const x = x0 + i * (chip + 4);
            centre[p.port] = x + chip / 2;
            const c = ROLE[p.role];
            const short = p.port.replace(/^ether/, 'e').replace(/^sfp-sfpplus/, 'sfp+');
            return (
               <g key={p.port}>
                  <rect x={x} y={44} width={chip} height={32} rx={5} fill={c.fill} stroke={c.stroke} strokeWidth={1.2} />
                  <text x={x + chip / 2} y={58} textAnchor="middle" fontSize={chip < 40 ? 9 : 10} fontWeight={700} fill={c.text}>
                     {chip < 40 ? short : p.port}
                  </text>
                  <text x={x + chip / 2} y={70} textAnchor="middle" fontSize={8.5} fill={c.text}>
                     {p.role === 'wan' ? (chip < 40 ? 'net' : 'internet') : chip < 40 ? (p.role === 'customer' ? 'cust.' : p.role) : p.role}
                  </text>
               </g>
            );
         })}
      </g>
   );
   return { nodes, centre };
}

/** Isolated layout (the hEX): one target per kind of port. */
const IsolatedDiagram: React.FC<{ ports: WiringPort[]; title: string }> = ({ ports, title }) => {
   const { nodes, centre } = routerBar(ports, title);
   const wan = ports.find((p) => p.role === 'wan');
   const customers = ports.filter((p) => p.role === 'customer');
   const trunks = ports.filter((p) => p.role === 'trunk');
   const owner = ports.find((p) => p.role === 'owner');
   const Y = 150;
   return (
      <Svg viewBox="0 0 640 250" role="img" aria-label={`${title} wiring`}>
         {nodes}
         {wan && line(centre[wan.port], 76, 80, Y, ROLE.wan.stroke)}
         {box(20, Y, 120, 56, 'wan', 'Internet', 'modem / fiber line', `into ${wan?.port ?? 'ether1'}`)}
         {customers.map((p) => line(centre[p.port], 76, 240, Y, ROLE.customer.stroke))}
         {box(170, Y, 140, 56, 'customer', 'Access point', 'main vendo Wi-Fi; the', 'NodeMCU joins this Wi-Fi')}
         {trunks.map((p) => line(centre[p.port], 76, 410, Y, ROLE.trunk.stroke))}
         {box(340, Y, 140, 56, 'trunk', 'Switch', 'then one access point', 'per sub-vendo (its VLAN)')}
         {owner && line(centre[owner.port], 76, 570, Y, ROLE.owner.stroke)}
         {owner && box(510, Y, 120, 56, 'owner', 'Your laptop', 'plain internet,', 'no portal')}
         <text x={320} y={238} textAnchor="middle" fontSize={10} fill="#4b5563">
            Customer ports also take a device plugged straight in. A plain laptop on the trunk port gets nothing.
         </text>
      </Svg>
   );
};

/** Flat layout (RB4011, CCR): internet in, every other port serves customers. */
const FlatDiagram: React.FC<{ ports: WiringPort[]; title: string }> = ({ ports, title }) => {
   const { nodes, centre } = routerBar(ports, title);
   const wan = ports.find((p) => p.role === 'wan');
   const lan = ports.filter((p) => p.role !== 'wan');
   const Y = 150;
   const first = lan[0] ? centre[lan[0].port] : 200;
   const last = lan[lan.length - 1] ? centre[lan[lan.length - 1].port] : 500;
   return (
      <Svg viewBox="0 0 640 250" role="img" aria-label={`${title} wiring`}>
         {nodes}
         {wan && line(centre[wan.port], 76, 80, Y, ROLE.wan.stroke)}
         {box(20, Y, 120, 56, 'wan', 'Internet', 'modem / fiber line', `into ${wan?.port ?? 'ether1'}`)}
         {/* a bracket under every customer port, then one line down */}
         <path d={`M ${first} 76 L ${first} 100 L ${last} 100 L ${last} 76`} fill="none" stroke={ROLE.customer.stroke} strokeWidth={2} />
         {line((first + last) / 2, 100, 380, Y, ROLE.customer.stroke)}
         {box(240, Y, 280, 56, 'customer', 'Access points and customers', 'any of these ports; the NodeMCU joins the Wi-Fi', 'or plugs into one of them')}
         <text x={320} y={238} textAnchor="middle" fontSize={10} fill="#4b5563">
            This model runs flat: no separate trunk or owner port. Sub-vendo VLANs are not switched on by default here.
         </text>
      </Svg>
   );
};

/** LAN ports per model -- mirrors server/src/utils/routerModels.ts (the server is the source of truth). */
export const MIKROTIK_LAN_PORTS: Record<string, readonly string[]> = {
   'hex-rb750gr3': ['ether2', 'ether3', 'ether4', 'ether5'],
   rb4011: ['ether2', 'ether3', 'ether4', 'ether5', 'ether6', 'ether7', 'ether8', 'ether9', 'ether10', 'sfp-sfpplus1'],
   ccr2004: ['ether2', 'ether3', 'ether4', 'ether5', 'ether6', 'ether7', 'ether8', 'ether9', 'ether10', 'ether11', 'ether12', 'ether13', 'ether14', 'ether15', 'ether16', 'sfp-sfpplus1', 'sfp-sfpplus2'],
   ccr2116: ['ether2', 'ether3', 'ether4', 'ether5', 'ether6', 'ether7', 'ether8', 'ether9', 'ether10', 'ether11', 'ether12', 'ether13', 'sfp-sfpplus1', 'sfp-sfpplus2', 'sfp-sfpplus3', 'sfp-sfpplus4'],
};
export const MIKROTIK_TITLE: Record<string, string> = {
   'hex-rb750gr3': 'MikroTik hEX RB750Gr3',
   rb4011: 'MikroTik RB4011',
   ccr2004: 'MikroTik CCR2004',
   ccr2116: 'MikroTik CCR2116',
};

/**
 * The port layout of a REGISTERED MikroTik, from what is saved on it -- the
 * same rule the server applies (resolvePortRoles): LAN ports minus the WAN and
 * the owner port; the operator's per-port choices win, otherwise the first two
 * are customer (access) ports and the rest trunk. Isolation off = flat.
 */
export function mikrotikWiringFor(router: {
   model?: string | null;
   wanPort?: string | null;
   ownerPort?: string | null;
   lanIsolation?: boolean | null;
   portRoles?: { port: string; role: 'access' | 'trunk' }[] | null;
} | null | undefined): { ports: WiringPort[]; isolated: boolean; title: string } | null {
   const model = router?.model ?? '';
   const lan = MIKROTIK_LAN_PORTS[model];
   if (!lan) return null;
   const wan = router?.wanPort || 'ether1';
   const owner = router?.ownerPort || null;
   const isolated = router?.lanIsolation === true;
   const ports: WiringPort[] = [{ port: wan, role: 'wan' }];
   const chosen = new Map((router?.portRoles ?? []).map((o) => [o.port, o.role] as const));
   lan
      .filter((p) => p !== wan && p !== owner)
      .forEach((p, i) => {
         if (!isolated) return ports.push({ port: p, role: 'customer' });
         const role = chosen.get(p) ?? (i < 2 ? 'access' : 'trunk');
         ports.push({ port: p, role: role === 'access' ? 'customer' : 'trunk' });
      });
   if (owner && lan.includes(owner)) ports.push({ port: owner, role: 'owner' });
   return { ports, isolated, title: MIKROTIK_TITLE[model] ?? 'MikroTik' };
}

const MikrotikWiring: React.FC<{ ports: WiringPort[]; isolated: boolean; title: string }> = ({ ports, isolated, title }) => (
   <Wrap data-testid="mikrotik-wiring">
      {isolated ? <IsolatedDiagram ports={ports} title={title} /> : <FlatDiagram ports={ports} title={title} />}
   </Wrap>
);

export default MikrotikWiring;

const Wrap = styled.div`
   border: 1px solid #e5e7eb;
   border-radius: 10px;
   padding: 8px 10px;
   background: #fafafa;
`;
const Svg = styled.svg`
   width: 100%;
   height: auto;
   display: block;
`;
